// ABOUTME: RTK Query endpoints for emailtemplate - customizable outgoing email templates.
// ABOUTME: Types listing (with allowed shortcodes) plus per-type lookup and CRUD, scoped by the active environment headers.

import { apiSlice } from "../createApiSlice";

const ApiName = "EmailTemplates";
const buildUrl = (endpoint) => `/emailtemplate${endpoint}`;

export const emailTemplateSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getEmailTemplateTypes: builder.query({
            query: () => ({
                url: buildUrl(`/types`),
                method: 'GET',
            }),
            providesTags: [{ type: "EmailTemplateTypes" }],
        }),
        getEmailTemplates: builder.query({
            // Without arguments every template of the scope; with type (+ optional
            // webshopCredentialsId) every country variant of that type for one saleschannel.
            query: (args) => {
                const params = new URLSearchParams();
                if (args?.type) params.set("type", args.type);
                if (args?.type && args?.webshopCredentialsId) params.set("webshopCredentialsId", args.webshopCredentialsId);
                const search = params.toString();
                return {
                    url: buildUrl(search ? `?${search}` : ``),
                    method: 'GET',
                };
            },
            providesTags: [{ type: ApiName }],
        }),
        getEmailTemplateByType: builder.query({
            // webshopCredentialsId scopes to one saleschannel; omitted/null = the default template.
            query: ({ type, webshopCredentialsId }) => ({
                url: buildUrl(`/type/${type}${webshopCredentialsId ? `?webshopCredentialsId=${webshopCredentialsId}` : ""}`),
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [
                { type: `${ApiName}Single`, id: `${arg.type}:${arg.webshopCredentialsId ?? "default"}` },
            ],
        }),
        getEmailTemplateSalesChannels: builder.query({
            query: () => ({
                url: buildUrl(`/saleschannels`),
                method: 'GET',
            }),
            providesTags: [{ type: "EmailTemplateSalesChannels" }],
        }),
        getEmailTemplateShipments: builder.query({
            // Search runs server-side; the backend always caps results at 10.
            query: (search) => ({
                url: buildUrl(`/shipments${search ? `?search=${encodeURIComponent(search)}` : ""}`),
                method: 'GET',
            }),
            providesTags: [{ type: "EmailTemplateShipments" }],
        }),
        getEmailTemplateShipmentPreview: builder.query({
            query: (shipmentId) => ({
                url: buildUrl(`/shipments/${shipmentId}/preview`),
                method: 'GET',
            }),
            providesTags: (result, error, arg) => [{ type: "EmailTemplateShipmentPreview", id: arg }],
        }),
        // Invalidation is skipped on error: a failed write changed nothing server-side,
        // and the refetch would needlessly unmount the editor (losing unsaved edits).
        addEmailTemplate: builder.mutation({
            query: (body) => ({
                url: buildUrl(``),
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error) => error ? [] : [{ type: ApiName }, { type: `${ApiName}Single` }],
        }),
        updateEmailTemplate: builder.mutation({
            query: ({ id, ...body }) => ({
                url: buildUrl(`/${id}`),
                method: 'PUT',
                body,
            }),
            invalidatesTags: (result, error) => error ? [] : [{ type: ApiName }, { type: `${ApiName}Single` }],
        }),
        deleteEmailTemplate: builder.mutation({
            query: (id) => ({
                url: buildUrl(`/${id}`),
                method: 'DELETE',
            }),
            invalidatesTags: (result, error) => error ? [] : [{ type: ApiName }, { type: `${ApiName}Single` }],
        }),
    })
});

export const {
    useGetEmailTemplateTypesQuery,
    useGetEmailTemplatesQuery,
    useGetEmailTemplateByTypeQuery,
    useGetEmailTemplateSalesChannelsQuery,
    useGetEmailTemplateShipmentsQuery,
    useGetEmailTemplateShipmentPreviewQuery,
    useAddEmailTemplateMutation,
    useUpdateEmailTemplateMutation,
    useDeleteEmailTemplateMutation,
} = emailTemplateSlice;
